import { useBoolean } from '@chakra-ui/hooks'
import { Popover, PopoverAnchor } from '@chakra-ui/popover'
import { Portal } from '@chakra-ui/portal'
import { IconProps } from '@chakra-ui/react'
import React, { MouseEventHandler } from 'react'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import {
  QuickActionButton,
  QuickActionButtonSizes,
} from '~/components/shared/QuickActionButton'
import { useKeyPresses } from '~/hooks/useKeyboard'
import { useUserClickedExternalElements } from '~/hooks/useUserClickedExternalElements'

import { Menu, MenuOptions, MenuPopoverWrapper } from './Menu'
import { MenuItem } from './MenuItem'

export type MangoButtonMenuItem<TValue> = MenuItem<TValue> & {
  buttonLabel?: string
}

type DropdownActionButtonProps<TValue> = {
  variant: QuickActionButtonVariants
  size?: QuickActionButtonSizes
  tooltipText: string | React.ReactNode
  icon: SVGComponent
  onButtonClick?: () => void
  items: MangoButtonMenuItem<TValue>[]
  selectedIndex?: number
  onSelect?: (value: TValue, index: number) => string | void
  onMenuOpenChange?: (isOpen: boolean) => void
  menuOptions?: MenuOptions
  iconProps?: IconProps
}

export const DropdownActionButton = <TValue,>({
  variant,
  size,
  tooltipText,
  icon,
  onButtonClick,
  items,
  onSelect,
  onMenuOpenChange,
  menuOptions,
  iconProps,
}: DropdownActionButtonProps<TValue>) => {
  const [, setInternalIndex] = React.useState<number>(0)
  const [menuOpen, setMenuOpen] = useBoolean(false)

  const containerRef = React.useRef<HTMLDivElement>(null)
  const buttonRef = React.useRef<HTMLButtonElement>(null)

  useKeyPresses({
    onPress: (e) => {
      e.key === 'Escape' && setMenuOpen.off()
    },
  })

  const handleMenuClose = () => {
    setMenuOpen.off()
    onMenuOpenChange?.(false)
  }
  useUserClickedExternalElements(
    [containerRef.current, buttonRef.current],
    true,
    handleMenuClose,
  )

  const handleButtonClick: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.stopPropagation()
    onButtonClick?.()
    setMenuOpen.toggle()
    onMenuOpenChange?.(!menuOpen)
  }
  const handleSelect = (value: TValue, index: number) => {
    onSelect?.(value, index)
    setInternalIndex(index)
    setMenuOpen.off()
    onMenuOpenChange?.(false)
  }

  return (
    <Popover isOpen={menuOpen} placement="bottom-start">
      <PopoverAnchor>
        <QuickActionButton
          ref={buttonRef}
          actionIcon={icon}
          tooltipText={tooltipText}
          tooltipDisabled={menuOpen}
          variant={variant}
          size={size}
          onClick={handleButtonClick}
          iconProps={iconProps}
        />
      </PopoverAnchor>
      <Portal>
        {menuOpen && (
          <MenuPopoverWrapper ref={containerRef}>
            <Menu items={items} onSelect={handleSelect} options={menuOptions} />
          </MenuPopoverWrapper>
        )}
      </Portal>
    </Popover>
  )
}
